/**
 * RBAC-2.3 · What a role change does to the people who hold it: a gained form and a lost form for
 * every row the display layer renders (FR-9e, FR-14, UX-DR11).
 *
 * The save diff renders a heading (the RBAC-2.2 label) and one of these sentences under it, never a
 * key: "12 people will stop seeing job cost and margin", not "removed cost.view".
 *
 * ⚠️ THESE ARE NOT FIXED STRINGS. The sentence changes grammar with the headcount, so each form is a
 * predicate that `consequenceFor()` puts into one shared frame:
 *  - 1 or more: "12 people will {form}." / "1 person will {form}."
 *  - 0:         "Nobody holds this role yet. Whoever is given it will {gained}." and, for a loss,
 *               "Nobody holds this role yet, so nobody loses this today." A role made a minute ago has
 *               no holders, and "0 people will stop seeing …" is what a naive template writes.
 *  - destructive, lost, 1 or more: "Nobody on this role will be able to {…} again." (the mockup's
 *    register for the sharper case).
 * Pluralisation lives in one helper, so no sentence can say "1 people".
 *
 * ⚠️ THE COUNT IS THE CALLER'S. It must come from the union of `users.role_id` and `user_roles`
 * (RBAC-3.6). A count from the legacy column alone under-reports who a revocation hits, which is
 * wrong in the direction of reassurance. Keeping it out of here leaves one place to get it wrong.
 *
 * The self-callout ("One of the 12 is you. You will lose …") needs nothing from this module: it is a
 * frame plus the RBAC-2.2 label, composed by RBAC-3.6.
 *
 * Sources, so each sentence has one:
 *  - field and lead permissions: `gained` / `lost` in `fieldPermissions.ts`;
 *  - `roles.administer`: `rolesAdministration.ts`;
 *  - everything else: `OWN_CONSEQUENCES` below, written from the RBAC-2.2 descriptions (which follow
 *    the call-site audit, not the key's name).
 * Those two modules write "Can …" / "Can no longer …"; `fromCanSentence()` turns them into predicates
 * and throws on any other shape, so a reworded source fails a test rather than a screen.
 *
 * A permission with no entry falls back to its RBAC-2.2 description, never to its key and never to an
 * empty string: a blank line under Losing reads as "nothing is lost". Asserted in the test.
 */

import { FIELD_PERMISSIONS } from './fieldPermissions';
import { ROLES_ADMINISTER } from './rolesAdministration';
import { HIDDEN_PERMISSIONS, PERMISSION_DISPLAY } from './permissionDisplay';

export type ConsequenceDirection = 'gained' | 'lost';

export interface Consequence {
  /** Follows "will": always "be able to …". */
  gained: string;
  /** Follows "will": "stop …ing …" or "no longer be able to …". */
  lost: string;
  /** Losing it renders as "Nobody on this role will be able to … again." */
  destructive?: boolean;
}

type Own = [key: string, gained: string, lost: string];

/** Rows whose sentences are not owned by `fieldPermissions.ts` or `rolesAdministration.ts`. */
const OWN: readonly Own[] = [
  // ── Projects ─────────────────────────────────────────────────────────────
  // None of these decides which projects a person can see, so no lost form says they lose projects.
  ['view_projects', 'be able to open the Redlines and Tasks pages and add notes to projects',
    'no longer be able to open the Redlines and Tasks pages or add notes to projects'],
  ['edit_projects', "be able to edit a project's title, overview, contributors and commission shares",
    "no longer be able to edit a project's title, overview, contributors or commission shares"],
  ['manage_projects', 'be able to move projects between stages directly and manage pipeline stages and finance options',
    'no longer be able to move projects between stages directly or manage pipeline stages and finance options'],
  ['project.manage_all', "be able to open a project's Contracts, Lender, Partner, Sales Team and Reports sections",
    "stop seeing a project's Contracts, Lender, Partner, Sales Team and Reports sections"],
  ['project.manage_team', "be able to edit the fields on a project's Overview tab",
    "no longer be able to edit the fields on a project's Overview tab"],
  ['project.view_all', 'be able to see redline pricing, including Commercial Redlines',
    'stop seeing redline pricing and Commercial Redlines'],
  ['project.view_team', 'be able to see pricing on the residential redline pages',
    'stop seeing pricing on the residential redline pages'],
  ['project.send_email', 'be able to send and read email from a project',
    "stop seeing a project's Emails section"],
  ['design.use', 'be able to create solar designs and build proposals from them',
    "stop seeing the Design section on a project's Proposals tab"],

  // ── Pipelines ────────────────────────────────────────────────────────────
  ['edit_pipeline', 'be able to drag projects between stages on the residential board',
    'no longer be able to drag projects between stages on the residential board'],
  ['pipeline.manage_all', 'be able to change pipeline stages and filter the boards by team and owner',
    'no longer be able to change pipeline stages or filter the boards by team and owner'],
  ['view_commercial_pipeline', 'be able to see the commercial pipelines and Commercial Redlines',
    'stop seeing the commercial pipelines in the sidebar and on the dashboard'],
  ['view_residential_pipeline', 'be able to see the residential pipelines',
    'stop seeing the residential pipelines in the sidebar and on the dashboard'],
  ['view_onboarding_pipeline', 'be able to see the onboarding pipelines and pages',
    'stop seeing the onboarding pipelines and pages'],

  // ── Contracts ────────────────────────────────────────────────────────────
  // The mockup says "on their projects"; it is every project they can open, so it says that.
  ['view_contracts', 'be able to see contracts on the projects they can open',
    "stop seeing a project's contracts and lender details"],
  ['manage_contracts', 'be able to issue change orders and choose who signs them',
    'no longer be able to issue change orders or choose who signs them'],

  // ── Finance & cost ───────────────────────────────────────────────────────
  ['view_finance', 'be able to see project finance, commission plans and commission shares',
    'stop seeing project finance, commission plans and commission shares'],
  ['edit_finance', 'be able to edit project finance and set up how commission is paid',
    'no longer be able to edit project finance or commission set-up'],
  // The design's own example. It sat on view_finance in the mockup; cost.view is what gates it.
  ['cost.view', 'be able to see job cost and margin',
    'stop seeing job cost and margin'],
  ['cost.actual.record', 'be able to record actual costs against a job',
    'no longer be able to record actual costs against a job'],
  ['cost.category.manage', 'be able to manage the categories costs are filed under',
    'no longer be able to change cost categories'],
  ['cost.rate_card.manage', 'be able to create and edit rate cards and installer costs',
    'no longer be able to change rate cards or installer costs'],
  ['cost.standard_rate.accept', 'be able to set the standard overhead rate',
    'no longer be able to set the standard overhead rate'],

  // ── Commission (commission.export is hidden: it gates nothing) ───────────
  ['commission.view', "be able to see everyone's commission, not only their own",
    "stop seeing anyone's commission but their own"],
  ['commission.approve', 'be able to approve commission',
    'no longer be able to approve commission'],
  ['commission.pay', 'be able to mark approved commission paid',
    'no longer be able to mark commission paid'],
  ['commission.adjust', 'be able to adjust, reverse or void a commission line',
    'no longer be able to change a calculated commission amount'],
  ['comp.agreement.manage', 'be able to create and edit comp agreement templates',
    'no longer be able to change comp agreement templates'],

  // ── Reporting (the field reports come from fieldPermissions.ts) ──────────
  ['reporting.admin', 'be able to open the Reporting and Tracking pages',
    'stop seeing the Reporting and Tracking pages'],

  // ── Support tickets ──────────────────────────────────────────────────────
  ['manage_tickets', 'be able to update any support ticket in the account',
    'no longer be able to update tickets they did not raise and are not assigned'],

  // ── Checklists ───────────────────────────────────────────────────────────
  ['manage_checklists', 'be able to restore deleted checklist items',
    'no longer be able to restore deleted checklist items'],

  // ── Teams ────────────────────────────────────────────────────────────────
  ['edit_teams', 'be able to change who manages a team',
    'no longer be able to change who manages a team'],
  ['manage_teams', 'be able to set who manages a team',
    'no longer be able to set who manages a team'],

  // ── People ───────────────────────────────────────────────────────────────
  ['manage_users', 'be able to add people to the account and manage Knock Mode invites',
    'no longer be able to add people to the account or manage Knock Mode invites'],
  ['user.manage_all', 'be able to edit, remove and change the role of anyone in the account',
    'no longer be able to edit, remove or change the role of anyone in the account'],
  ['user.manage_team', 'be able to add people to the account',
    'no longer be able to add people to the account'],

  // ── Administration ───────────────────────────────────────────────────────
  // The four reserved rows still need both forms: a superadmin editing a Joules role uses this diff.
  ['manage_roles', 'be able to open admin pages such as Contracts and the automation builder, and delete projects',
    'no longer be able to open admin pages or delete projects'],
  ['system.override', 'be able to pass every permission check in the app and the database',
    'stop passing every permission check, keeping only what their other permissions give them'],
  ['manage_settings', 'be able to open Settings, administer people, and change commission, finance and account settings',
    'no longer be able to open Settings, administer people or change account settings'],
  ['settings.admin', 'be able to use every admin page, including project deletion',
    'no longer be able to use the admin pages or delete projects'],
  ['tenant.manage', 'be able to manage the account itself, including superadmin controls and project deletion',
    'no longer be able to manage the account, use superadmin controls or delete projects'],
];

/** "Can X." → "be able to X"; "Can no longer X." → "no longer be able to X". Anything else throws. */
export function fromCanSentence(sentence: string): string {
  const lost = /^Can no longer (.+)\.$/.exec(sentence);
  if (lost) return `no longer be able to ${lost[1]}`;
  const gained = /^Can (.+)\.$/.exec(sentence);
  if (gained) return `be able to ${gained[1]}`;
  throw new Error(`permissionConsequences: "${sentence}" is not a "Can …" sentence`);
}

export const OWN_CONSEQUENCES: Readonly<Record<string, Consequence>> = Object.fromEntries(
  OWN.map(([key, gained, lost]) => [key, { gained, lost }]),
);

function build(): Record<string, Consequence> {
  const out: Record<string, Consequence> = {};
  const add = (key: string, c: Consequence) => {
    if (out[key]) throw new Error(`permissionConsequences: ${key} declared twice`);
    out[key] = c;
  };
  for (const f of FIELD_PERMISSIONS) {
    add(f.key, { gained: fromCanSentence(f.gained), lost: fromCanSentence(f.lost), destructive: f.kind === 'destructive' || undefined });
  }
  add(ROLES_ADMINISTER.key, { gained: fromCanSentence(ROLES_ADMINISTER.gained), lost: fromCanSentence(ROLES_ADMINISTER.lost) });
  for (const [key, c] of Object.entries(OWN_CONSEQUENCES)) add(key, c);
  return out;
}

/** Every visible permission's two forms, keyed by permission. */
export const PERMISSION_CONSEQUENCES: Readonly<Record<string, Consequence>> = build();

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`);

/**
 * The sentence the save diff prints under a permission's heading. `count` is how many people hold
 * the role being saved, computed by the caller. `table` exists for the fallback test.
 */
export function consequenceFor(
  key: string,
  direction: ConsequenceDirection,
  count: number,
  table: Readonly<Record<string, Consequence>> = PERMISSION_CONSEQUENCES,
): string {
  if (!Number.isInteger(count) || count < 0) throw new Error(`permissionConsequences: ${count} is not a headcount`);
  const c = table[key];
  if (!c) return fallback(key);
  if (count === 0) {
    return direction === 'gained'
      ? `Nobody holds this role yet. Whoever is given it will ${c.gained}.`
      : 'Nobody holds this role yet, so nobody loses this today.';
  }
  if (direction === 'lost' && c.destructive) {
    return `Nobody on this role will be able to ${c.lost.replace(/^no longer be able to /, '')} again.`;
  }
  return `${people(count)} will ${direction === 'gained' ? c.gained : c.lost}.`;
}

/**
 * RBAC-8.1 · FR-27. The same sentence, told about a past edit: the role's history renders each version
 * "in the same language as the pre-save diff". The predicates are the ones above, unchanged; only the
 * frame moves into the past, with `would` carrying every form ("be able to …", "stop …ing …", "no
 * longer be able to …") without conjugating any of them.
 *
 * ⚠️ `count` is the holder count RECORDED WITH THE VERSION, not today's. Today's would put words in
 * the past's mouth.
 */
export function consequenceThen(
  key: string,
  direction: ConsequenceDirection,
  count: number,
  table: Readonly<Record<string, Consequence>> = PERMISSION_CONSEQUENCES,
): string {
  if (!Number.isInteger(count) || count < 0) throw new Error(`permissionConsequences: ${count} is not a headcount`);
  const c = table[key];
  if (!c) return fallback(key);
  if (count === 0) {
    return direction === 'gained'
      ? `Nobody held this role then. Whoever was given it would ${c.gained}.`
      : 'Nobody held this role then, so nobody lost this.';
  }
  if (direction === 'lost' && c.destructive) {
    return `At the time, nobody on this role would be able to ${c.lost.replace(/^no longer be able to /, '')} again.`;
  }
  return `At the time, ${people(count)} would ${direction === 'gained' ? c.gained : c.lost}.`;
}

/** The RBAC-2.2 description, or a sentence that names no key. Never the key, never empty. */
function fallback(key: string): string {
  const description = PERMISSION_DISPLAY[key]?.description;
  if (description?.trim()) return description;
  if (HIDDEN_PERMISSIONS[key]) return 'A permission that changes nothing in the product today.';
  return 'A permission this screen has no description for yet.';
}
